import { RestClient } from "./restClient";

export interface OrgSyncFetchResponse {
  prompt: string;
  org_sync: boolean;
}

export interface SyncedPerson {
  company_id?: string;
  plugin_slug?: string;
  source_key: string;
  email: string | null;
  display_name: string | null;
  dept_source_keys: string[];
  role_source_keys: string[];
  is_artie_member?: boolean;
}

export interface SyncedRole {
  company_id?: string;
  plugin_slug?: string;
  source_key: string;
  artie_role_name: string;
}

export class OrgSyncApi {
  constructor(private client: RestClient) {}

  fetch(): Promise<OrgSyncFetchResponse> {
    return this.client.post<OrgSyncFetchResponse>("/core/v1/org-sync/fetch", {});
  }

  async people(params?: {
    page?: number;
    limit?: number;
    q?: string;
  }): Promise<{
    people: SyncedPerson[];
    pagination: {
      page: number;
      limit: number;
      total: number;
      total_pages: number;
    };
  }> {
    const page = params?.page ?? 1;
    const limit = params?.limit ?? 25;
    const qs = new URLSearchParams({
      page: String(page),
      limit: String(limit),
    });
    const q = params?.q?.trim();
    if (q) qs.set("q", q);
    const raw = await this.client.get<{
      people?: SyncedPerson[];
      pagination?: {
        page: number;
        limit: number;
        total: number;
        total_pages: number;
      };
    }>(`/core/v1/org-sync/people?${qs.toString()}`);
    const people = raw.people ?? [];
    const pagination = raw.pagination ?? {
      page,
      limit,
      total: people.length,
      total_pages: Math.max(1, Math.ceil(people.length / limit) || 1),
    };
    return { people, pagination };
  }

  roles(): Promise<{ roles: SyncedRole[] }> {
    return this.client.get<{ roles: SyncedRole[] }>("/core/v1/org-sync/roles");
  }

  /** Run the org-sync turn in the background and wait until it finishes.
   *  Does not open chat. The temporary session is deleted afterwards. */
  async run(): Promise<void> {
    const started = await this.fetch();
    const resp = await this.client.rawResponse("/v1/chat", {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Accept: "text/event-stream",
      },
      body: JSON.stringify({
        text: started.prompt,
        payload: { org_sync: true },
      }),
    });
    if (!resp.ok) {
      let detail = `Chat request failed (${resp.status})`;
      try {
        const text = await resp.text();
        if (text) {
          try {
            const parsed = JSON.parse(text) as { detail?: unknown };
            if (typeof parsed.detail === "string" && parsed.detail.trim()) {
              detail = parsed.detail;
            } else {
              detail = text.slice(0, 400);
            }
          } catch {
            detail = text.slice(0, 400);
          }
        }
      } catch {
        /* keep status fallback */
      }
      throw new Error(detail);
    }
    const sessionId = await drainOrgSyncStream(resp);
    if (!sessionId) return;
    try {
      await this.client.delete(`/core/v1/sessions/${encodeURIComponent(sessionId)}`);
    } catch {
      // Leftover session is harmless; the user never opened this chat.
    }
  }
}

async function drainOrgSyncStream(resp: Response): Promise<string | null> {
  if (!resp.body) return null;
  const reader = resp.body.getReader();
  const decoder = new TextDecoder();
  let buf = "";
  let sessionId: string | null = null;
  let error: string | null = null;
  for (;;) {
    const { done, value } = await reader.read();
    if (done) break;
    buf += decoder.decode(value, { stream: true });
    const parts = buf.split("\n\n");
    buf = parts.pop() ?? "";
    for (const part of parts) {
      const parsed = readSseData(part);
      if (!parsed) continue;
      const sid = parsed.session_id ?? parsed.payload?.session_id;
      if (typeof sid === "string" && sid) sessionId = sid;
      if (parsed.type === "error" || parsed.type === "bot_error") {
        error =
          parsed.message ??
          (typeof parsed.payload?.message === "string" ? parsed.payload.message : null) ??
          "Fetch failed";
      }
    }
  }
  if (error) throw new Error(error);
  return sessionId;
}

function readSseData(part: string): {
  type?: string;
  session_id?: string;
  message?: string;
  payload?: { session_id?: string; message?: string };
} | null {
  for (const line of part.split("\n")) {
    if (!line.startsWith("data:")) continue;
    const raw = line.slice(5).trim();
    if (!raw || raw === "[DONE]") continue;
    try {
      return JSON.parse(raw) as {
        type?: string;
        session_id?: string;
        message?: string;
        payload?: { session_id?: string; message?: string };
      };
    } catch {
      return null;
    }
  }
  return null;
}
